import React, { useEffect, useState } from "react";
import { Card, Badge, Spinner } from "react-bootstrap";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL;

function MyChainUpLine() {
  const navigate = useNavigate();
  const [mobile, setMobile] = useState("");
  const [associateName, setAssociateName] = useState("");
  const [checking, setChecking] = useState(false);
  const [loading, setLoading] = useState(false);
  const [chainData, setChainData] = useState(null);
  const [showChain, setShowChain] = useState(false);

  const getAuthToken = () => localStorage.getItem("token");
  
  useEffect(() => {
    const fetchAssociateName = async () => {
      if (mobile.length === 10) {
        setChecking(true);
        setAssociateName("");

        try {
          const token = getAuthToken();
          if (!token) {
            Swal.fire({
              icon: "error",
              title: "Authentication Error",
              text: "Authentication token not found. Please log in again.",
            });
            setChecking(false);
            return;
          }

          const payload = {
            parentid: mobile,
            type: "associate",
          };

          const response = await fetch(`${API_URL}/check-parentid-name`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
          });

          const data = await response.json();

          if (response.ok && data.status === "1" && data.data?.username) {
            setAssociateName(data.data.username);
          } else {
            setAssociateName("");
          }
        } catch (error) {
          console.error("Error fetching associate name:", error);
          setAssociateName("");
        } finally {
          setChecking(false);
        }
      } else {
        setAssociateName("");
      }
    };

    fetchAssociateName();
  }, [mobile]);

  const fetchChainData = async (e) => {
    if (e) e.preventDefault();

    if (!mobile) {
      Swal.fire({
        icon: "warning",
        title: "Missing Input",
        text: "Please enter a mobile number",
      });
      return;
    }

    setLoading(true);
    setShowChain(false);
    setChainData(null);

    try {
      const token = getAuthToken();
      const response = await axios.get(
        `${API_URL}/myteam-parent-chain?mobile=${mobile}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.status === "1") {
        setChainData(response.data);
        setShowChain(true);
      } else {
        Swal.fire({
          icon: "info",
          title: "Sorry!",
          text: response.data.message || "No data found",
        });
      }
    } catch (error) {
      console.error("Error fetching chain data:", error);
      Swal.fire({
        icon: "warning",
        title: "Warning!",
        text: "No data found, please try another.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle box click with URL parameters
  const handleBoxClick = (mobileNumber, userName, userType) => {
    // Navigate to details page with mobile number as URL parameter
    navigate(`/myteam-child-chain-down-line-details?mobile=${mobileNumber}`);
  };

  const ParentChain = () => {
    if (!chainData || !chainData.parents) return null;

    return (
      <div className="parent-chain-container">
        <div className="chain-flow">
          {chainData.parents.map((parent, index) => (
            <div key={parent.id} className="chain-item-wrapper">
              {index > 0 && (
                <div className="chain-connector"></div>
              )}
              <div 
                className="chain-item card shadow-sm mb-3 clickable-chain-item"
                onClick={() => handleBoxClick(parent.mobile, parent.username, parent.user_type)}
                style={{ cursor: "pointer" }}
              >
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center mb-2">
                        <strong className="fs-5 me-2">
                          {parent.username
                            ? parent.username.charAt(0).toUpperCase() + parent.username.slice(1).toLowerCase()
                            : ""}
                        </strong>
                        <Badge bg="dark">L{parent.level}</Badge>
                      </div>
                      <div className="d-flex flex-wrap gap-2 text-muted">
                        <span>📱 {parent.mobile}</span>
                      </div>
                      {parent.parent_id && (
                        <div className="mt-2">
                          <small className="text-muted">
                            Parent ID: {parent.parent_id}
                          </small>
                        </div>
                      )}

                      {parent.buysqrt && (
                        <div className="mt-2">
                          <small className="text-muted">
                            Total: {Number(parent.buysqrt).toFixed(2)} BuySQRT
                          </small>
                        </div>
                      )}

                      {parent.user_type && (
                        <div className="mt-2">
                          <small className="text-muted">
                            User Type: {parent.user_type?.charAt(0).toUpperCase() + parent.user_type?.slice(1).toLowerCase()}
                          </small>
                        </div>
                      )}
                    </div>
                    <div className="text-end">
                      <div className="level-indicator">
                        <Badge bg="secondary" className="fs-6">
                          Level {parent.level}
                        </Badge>
                      </div>
                      <div className="mt-2">
                        <Badge bg="info" className="click-indicator">
                          Click for Details →
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Current User Card - Also clickable */}
        <div className="text-center mb-4">
          <div 
            className="current-user-card p-3 bg-primary text-white rounded shadow clickable-chain-item"
            onClick={() => handleBoxClick(chainData.child_mobile, associateName, "current")}
            style={{ cursor: "pointer", maxWidth: "300px", margin: "0 auto" }}
          >
            <h5 className="mb-1">Current User</h5>
            <strong className="fs-4">
              {(associateName || chainData.child_mobile)
                ? (associateName || chainData.child_mobile).charAt(0).toUpperCase() +
                (associateName || chainData.child_mobile).slice(1).toLowerCase()
                : ""}
            </strong>

            <div className="mt-1">
              <Badge bg="light" text="dark">📱 {chainData.child_mobile}</Badge>
            </div>

            <div className="mt-1">
              Total : {Number(chainData.total_buysqrt).toFixed(2)} BuySQRT
            </div>
            
            <div className="mt-2">
              <Badge bg="warning" text="dark" className="click-indicator">
                Click for Details →
              </Badge>
            </div>
          </div>
        </div>

        <div className="text-center mt-4 p-3 bg-light rounded">
          <h6 className="mb-2">Chain Summary</h6>
          <div className="d-flex justify-content-center gap-3">
            <Badge bg="primary" className="fs-6">
              Total Levels: {chainData.total_levels}
            </Badge>
            <Badge bg="success" className="fs-6">
              Total Parents: {chainData.parents.length}
            </Badge>
          </div>
        </div>
      </div>
    );
  };

  // return (
  //   <div className="padding_15">
  //     <style>
  //       {`
  //         .chain-flow {
  //           position: relative;
  //           max-width: 800px;
  //           margin: 0 auto;
  //         }
          
  //         .chain-item-wrapper {
  //           position: relative;
  //         }
          
  //         .chain-connector {
  //           position: absolute;
  //           left: 50%;
  //           top: -15px;
  //           width: 2px;
  //           height: 30px;
  //           background: linear-gradient(to bottom, #007bff, #28a745);
  //           transform: translateX(-50%);
  //           z-index: 1;
  //         }
          
  //         .current-user-card {
  //           border: 2px solid #007bff;
  //           transition: all 0.3s ease;
  //         }
          
  //         .chain-item {
  //           transition: transform 0.2s ease;
  //           border-left: 4px solid #007bff;
  //         }
          
  //         .chain-item:hover {
  //           transform: translateY(-2px);
  //           box-shadow: 0 4px 8px rgba(0,0,0,0.15) !important;
  //         }
          
  //         .level-indicator {
  //           min-width: 80px;
  //         }
          
  //         .clickable-chain-item {
  //           transition: all 0.3s ease;
  //         }
          
  //         .clickable-chain-item:hover {
  //           transform: translateY(-3px);
  //           box-shadow: 0 6px 12px rgba(0,0,0,0.15) !important;
  //           border-color: #28a745;
  //         }
          
  //         .click-indicator {
  //           font-size: 0.7rem;
  //           animation: pulse 2s infinite;
  //         }
          
  //         @keyframes pulse {
  //           0% { opacity: 0.7; }
  //           50% { opacity: 1; }
  //           100% { opacity: 0.7; }
  //         }
  //       `}
  //     </style>

  //     <div className="row">
  //       <div className="col-md-12">
  //         <Card>
  //           <Card.Header>
  //             <h4 className="mb-0">My Upline Chain</h4>
  //           </Card.Header>
  //           <Card.Body>
  //             <div className="row">
  //               <div className="col-md-6 offset-md-3">
  //                 <Card>
  //                   <Card.Body>
  //                     <form onSubmit={fetchChainData}>
  //                       <div className="mb-3">
  //                         <label className="form-label fw-bold">Enter Mobile Number</label>
  //                         <input
  //                           type="text"
  //                           className="form-control"
  //                           placeholder="Enter Mobile Number"
  //                           value={mobile}
  //                           onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
  //                           maxLength="10"
  //                         />
  //                       </div>

  //                       <div className="text-center mb-3">
  //                         {checking && (
  //                           <div className="d-flex align-items-center justify-content-center">
  //                             <Spinner animation="border" size="sm" className="me-2" />
  //                             <span style={{ color: "gray" }}>Checking associate name...</span>
  //                           </div>
  //                         )}
  //                         {associateName && (
  //                           <div className="alert alert-success py-2 mb-0">
  //                             <strong>✓ Name:</strong> {associateName}
  //                           </div>
  //                         )}
  //                       </div>

  //                       <div className="submitbutton">
  //                         <button
  //                           type="submit"
  //                           className="btn btn-primary w-100"
  //                           disabled={loading}
  //                         >
  //                           {loading ? (
  //                             <>
  //                               <Spinner animation="border" size="sm" className="me-2" />
  //                               Loading...
  //                             </>
  //                           ) : (
  //                             'Show Upline Chain 11 Levels'
  //                           )}
  //                         </button>
  //                       </div>
  //                     </form>
  //                   </Card.Body>
  //                 </Card>
  //               </div>
  //             </div>
  //             {showChain && chainData && (
  //               <div className="row mt-4">
  //                 <div className="col-md-12">
  //                   <Card>
  //                     <Card.Header className="d-flex justify-content-between align-items-center">
  //                       <h5 className="mb-0">
  //                         Upline Chain for: {chainData.child_mobile}
  //                         {associateName && ` (${associateName})`}
  //                       </h5>
  //                       <div>
  //                         <Badge bg="primary" className="me-2">
  //                           Levels: {chainData.total_levels}
  //                         </Badge>
  //                         <Badge bg="success">
  //                           Parents: {chainData.parents.length}
  //                         </Badge>
  //                       </div>
  //                     </Card.Header>
  //                     <Card.Body>
  //                       <div className="p-3">
  //                         <div className="alert alert-info mb-3">
  //                           <strong>💡 Tip:</strong> Click on any box below to view detailed downline information for that user.
  //                         </div>
  //                         <ParentChain />
  //                       </div>
  //                     </Card.Body>
  //                   </Card>
  //                 </div>
  //               </div>
  //             )}
  //           </Card.Body>
  //         </Card>
  //       </div>
  //     </div>
  //   </div>
  // );

return (
    <>
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #f0f6ff 0%, #e6f0fa 100%)",
          padding: "30px 24px",
          fontFamily: "'Poppins', 'DM Sans', sans-serif",
        }}
      >
        <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
          {/* 🎯 HEADER CARD */}
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              boxShadow: "0 8px 24px rgba(30, 87, 153, 0.08)",
              border: "1.5px solid #e2e8f0",
              padding: "20px 24px",
              display: "flex",
              alignItems: "center",
              gap: "16px",
              marginBottom: "20px",
            }}
          >
            <div
              style={{
                width: "52px",
                height: "52px",
                borderRadius: "14px",
                background: "linear-gradient(135deg, #1e5799, #2b6cb0)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#ffffff",
                fontSize: "24px",
                boxShadow: "0 8px 20px rgba(30, 87, 153, 0.3)",
                flexShrink: 0,
              }}
            >
              ⛓️
            </div>
            <div style={{ flex: 1 }}>
              <h1
                style={{
                  margin: 0,
                  fontSize: "24px",
                  fontWeight: "800",
                  color: "#1e3a5f",
                  letterSpacing: "-0.5px",
                }}
              >
                My Upline Chain
              </h1>
              <p
                style={{
                  margin: "2px 0 0",
                  fontSize: "13px",
                  color: "#64748b",
                  fontWeight: "500",
                }}
              >
                View your 11-level upline hierarchy
              </p>
            </div>
          </div>

          {/* 🎯 MAIN CARD */}
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              boxShadow: "0 8px 24px rgba(30, 87, 153, 0.08)",
              border: "1.5px solid #e2e8f0",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "5px",
                background: "linear-gradient(90deg, #1e5799, #2b6cb0, #1e5799)",
              }}
            />

            <div style={{ padding: "24px" }}>
              {/* 🎯 SEARCH FORM */}
              <div
                style={{
                  maxWidth: "600px",
                  margin: "0 auto 24px",
                }}
              >
                <form onSubmit={fetchChainData}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: "700",
                      color: "#1e3a5f",
                      marginBottom: "8px",
                    }}
                  >
                    Enter Mobile Number
                  </label>
                  <input
                    type="text"
                    placeholder="Enter Mobile Number"
                    value={mobile}
                    onChange={(e) =>
                      setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))
                    }
                    maxLength="10"
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      borderRadius: "10px",
                      border: "1.5px solid #e2e8f0",
                      outline: "none",
                      fontSize: "14px",
                      marginBottom: "12px",
                    }}
                  />

                  {checking && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                        marginBottom: "12px",
                        color: "#64748b",
                        fontSize: "13px",
                      }}
                    >
                      <span
                        style={{
                          width: "14px",
                          height: "14px",
                          border: "2px solid #cbd5e1",
                          borderTop: "2px solid #2b6cb0",
                          borderRadius: "50%",
                          display: "inline-block",
                          animation: "spin 0.8s linear infinite",
                        }}
                      />
                      Checking associate name...
                    </div>
                  )}

                  {associateName && (
                    <div
                      style={{
                        padding: "10px 14px",
                        borderRadius: "10px",
                        background: "#dcfce7",
                        border: "1.5px solid #86efac",
                        color: "#166534",
                        fontSize: "13px",
                        fontWeight: "600",
                        marginBottom: "12px",
                      }}
                    >
                      ✓ Name: {associateName}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      width: "100%",
                      padding: "13px 32px",
                      borderRadius: "12px",
                      border: "none",
                      background: loading
                        ? "#cbd5e1"
                        : "linear-gradient(135deg, #1e5799, #2b6cb0)",
                      color: "#ffffff",
                      fontSize: "14.5px",
                      fontWeight: "800",
                      cursor: loading ? "not-allowed" : "pointer",
                      boxShadow: loading
                        ? "none"
                        : "0 8px 24px rgba(30, 87, 153, 0.3)",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "10px",
                    }}
                  >
                    {loading ? (
                      <>
                        <span
                          style={{
                            width: "16px",
                            height: "16px",
                            border: "2px solid rgba(255,255,255,0.3)",
                            borderTop: "2px solid #ffffff",
                            borderRadius: "50%",
                            display: "inline-block",
                            animation: "spin 0.8s linear infinite",
                          }}
                        />
                        Loading...
                      </>
                    ) : (
                      "Show Upline Chain 11 Levels"
                    )}
                  </button>
                </form>
              </div>

              {/* 🎯 CHAIN RESULT */}
              {showChain && chainData && (
                <div
                  style={{
                    padding: "20px",
                    background: "#f8fafc",
                    borderRadius: "14px",
                    border: "1.5px dashed #e2e8f0",
                  }}
                >
                  {/* Result header */}
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "10px",
                      paddingBottom: "16px",
                      marginBottom: "16px",
                      borderBottom: "1.5px dashed #e2e8f0",
                    }}
                  >
                    <h5
                      style={{
                        margin: 0,
                        color: "#1e3a5f",
                        fontWeight: "800",
                        fontSize: "16px",
                      }}
                    >
                      Upline Chain for: {chainData.child_mobile}
                      {associateName && ` (${associateName})`}
                    </h5>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <span
                        style={{
                          padding: "6px 14px",
                          borderRadius: "20px",
                          background:
                            "linear-gradient(135deg, #1e5799, #2b6cb0)",
                          color: "#ffffff",
                          fontSize: "12px",
                          fontWeight: "700",
                        }}
                      >
                        Levels: {chainData.total_levels}
                      </span>
                      <span
                        style={{
                          padding: "6px 14px",
                          borderRadius: "20px",
                          background:
                            "linear-gradient(135deg, #16a34a, #22c55e)",
                          color: "#ffffff",
                          fontSize: "12px",
                          fontWeight: "700",
                        }}
                      >
                        Parents: {chainData.parents.length}
                      </span>
                    </div>
                  </div>

                  {/* Tip */}
                  <div
                    style={{
                      padding: "12px 16px",
                      borderRadius: "10px",
                      background: "#eff6ff",
                      border: "1.5px solid #bfdbfe",
                      color: "#1e3a5f",
                      fontSize: "13px",
                      fontWeight: "600",
                      marginBottom: "20px",
                    }}
                  >
                    💡 <strong>Tip:</strong> Click on any box below to view
                    detailed downline information for that user.
                  </div>

                  {/* Parent Chain */}
                  <ParentChain />
                </div>
              )}

              {/* Empty state */}
              {!showChain && !loading && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "60px 20px",
                    color: "#94a3b8",
                  }}
                >
                  <div style={{ fontSize: "56px", marginBottom: "12px" }}>
                    ⛓️
                  </div>
                  <p
                    style={{
                      fontSize: "15px",
                      fontWeight: "600",
                      color: "#64748b",
                      margin: 0,
                    }}
                  >
                    Enter a mobile number above to view the upline chain
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Footer Copyright */}
          <p
            style={{
              textAlign: "center",
              color: "#94a3b8",
              fontSize: "12.5px",
              marginTop: "24px",
              fontWeight: "500",
            }}
          >
            © {new Date().getFullYear()} A2 Demo Real Estates Pvt Ltd. All
            Rights Reserved.
          </p>
        </div>
      </div>

      {/* 🎯 CHAIN CSS */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .chain-flow {
          position: relative;
          max-width: 800px;
          margin: 0 auto;
        }

        .chain-item-wrapper {
          position: relative;
        }

        .chain-connector {
          position: absolute;
          left: 50%;
          top: -15px;
          width: 2px;
          height: 30px;
          background: linear-gradient(to bottom, #1e5799, #2b6cb0);
          transform: translateX(-50%);
          z-index: 1;
        }

        .current-user-card {
          background: linear-gradient(135deg, #1e5799, #2b6cb0) !important;
          border: 2px solid #1e5799;
          transition: all 0.3s ease;
        }

        .chain-item {
          transition: transform 0.2s ease;
          border-left: 4px solid #1e5799 !important;
          background: #ffffff;
          border-radius: 12px !important;
          border-top: 1.5px solid #e2e8f0 !important;
          border-right: 1.5px solid #e2e8f0 !important;
          border-bottom: 1.5px solid #e2e8f0 !important;
        }

        .chain-item:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(30, 87, 153, 0.15) !important;
        }

        .level-indicator {
          min-width: 80px;
        }

        .clickable-chain-item {
          transition: all 0.3s ease;
        }

        .clickable-chain-item:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 24px rgba(30, 87, 153, 0.2) !important;
          border-color: #22c55e !important;
        }

        .click-indicator {
          font-size: 0.7rem;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0% { opacity: 0.7; }
          50% { opacity: 1; }
          100% { opacity: 0.7; }
        }
      `}</style>
    </>
  );
}

export default MyChainUpLine;